/**
 * Cliente API WhatsApp (Twilio) para el panel de admin.
 */

function getBaseUrl(): string {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  return '/api';
}

export interface WhatsAppStatus {
  connected: boolean;
  phoneNumber: string | null;
}

export async function getWhatsAppStatus(): Promise<{ success: boolean } & WhatsAppStatus> {
  const res = await fetch(`${getBaseUrl()}/whatsapp/status`, { credentials: 'include' });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Error al obtener estado de WhatsApp');
  return data;
}

export interface WhatsAppConversation {
  phone: string;
  lastBody: string;
  lastAt: string;
  lastDirection: 'inbound' | 'outbound';
  unreadCount: number;
}

export async function getWhatsAppConversations(): Promise<{
  success: boolean;
  conversations: WhatsAppConversation[];
}> {
  const res = await fetch(`${getBaseUrl()}/whatsapp/conversations`, { credentials: 'include' });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Error al listar conversaciones');
  return data;
}

export interface WhatsAppMessage {
  id: number;
  direction: 'inbound' | 'outbound';
  from: string;
  to: string;
  body: string;
  twilioSid: string | null;
  createdAt: string;
  readAt: string | null;
}

export async function getWhatsAppMessages(phone: string): Promise<{
  success: boolean;
  messages: WhatsAppMessage[];
}> {
  const res = await fetch(
    `${getBaseUrl()}/whatsapp/messages?phone=${encodeURIComponent(phone)}`,
    { credentials: 'include' }
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Error al listar mensajes');
  return data;
}

export async function markWhatsAppAsRead(phone: string): Promise<{ success: boolean }> {
  const res = await fetch(`${getBaseUrl()}/whatsapp/messages/mark-read`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ phone }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Error al marcar como leídos');
  return data;
}

export async function sendWhatsAppMessage(to: string, body: string): Promise<{ success: boolean; sid?: string }> {
  const res = await fetch(`${getBaseUrl()}/whatsapp/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ to, body }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Error al enviar mensaje');
  return data;
}

export async function saveWhatsAppConfig(config: {
  accountSid: string;
  authToken: string;
  phoneNumber: string;
}): Promise<{ success: boolean } & WhatsAppStatus> {
  const res = await fetch(`${getBaseUrl()}/whatsapp/config`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(config),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Error al guardar configuración');
  return data;
}
